import {
    Close,
    Code,
    DataObject,
    DeleteOutlined,
    DescriptionOutlined,
    ExpandMore,
    FormatAlignCenter,
    FormatAlignJustify,
    FormatAlignLeft,
    FormatAlignRight,
    FormatBold,
    FormatClear,
    FormatItalic,
    FormatListBulleted,
    FormatListNumbered,
    FormatQuote,
    FormatUnderlined,
    HorizontalRule,
    ImageOutlined,
    InsertLink,
    LinkOff,
    LockOutlined,
    Looks3,
    LooksOne,
    LooksTwo,
    PaletteOutlined,
    Redo,
    StrikethroughS,
    TableChart,
    TextFields,
    Undo,
} from '@mui/icons-material';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    ListSubheader,
    Menu,
    MenuItem,
    Popover,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Editor } from '@tiptap/react';
import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';

import { BrandingPanel } from './BrandingPanel';
import { ColorControl } from './ColorControl';
import { TableInsertDialog } from './TableInsertDialog';

import { fontFamilyLabel, quoteFontFamily } from './documentContentStyles';
import {
    DEFAULT_BRANDING,
    FONT_FAMILIES,
    LINE_HEIGHT_OPTIONS,
    PAGE_SIZES,
} from 'config/defaultBranding';
import {
    getCategoryLabelsForType,
    getMandatoryPlaceholdersForType,
    getPlaceholdersByCategoryForType,
} from 'config/placeholders';
import { MESSAGES } from 'constant/messages';
import useTranslate from 'hooks/useTranslate';
import type { BrandingSettings, PricingConfiguration, TemplateType } from 'types';

const toolbarButtonSx = (active = false) => ({
    textTransform: 'none' as const,
    fontWeight: 500,
    minWidth: 28,
    height: 28,
    minHeight: 28,
    px: 0.75,
    py: 0,
    borderRadius: '4px',
    border: 'none',
    color: '#444746',
    bgcolor: active ? '#d3e3fd' : 'transparent',
    boxShadow: 'none',
    '& .MuiSvgIcon-root': { fontSize: 18, width: 18, height: 18 },
    '&:hover': {
        bgcolor: active ? '#c2d7fc' : 'rgba(68, 71, 70, 0.08)',
        boxShadow: 'none',
    },
    '&.Mui-disabled': {
        boxShadow: 'none',
    },
});

const toolbarSelectSx = {
    height: 28,
    fontSize: 12,
    color: '#444746',
    bgcolor: '#fff',
    borderRadius: '4px',
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d1d5db' },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#9ca3af' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#8353E2', borderWidth: 1 },
    '& .MuiSelect-select': {
        py: 0,
        pl: 1,
        pr: '28px !important',
        minHeight: '0 !important',
        height: 28,
        display: 'flex',
        alignItems: 'center',
        lineHeight: '28px',
    },
    '& .MuiSelect-icon': { fontSize: 16, right: 4, color: '#444746' },
};

const menuPaperSx = {
    mt: 1,
    minWidth: 180,
    maxHeight: 360,
};

interface ToolbarActionProps {
    label: string;
    ariaLabel?: string;
    active?: boolean;
    disabled?: boolean;
    onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
    children: ReactNode;
}

function ToolbarAction({
    label,
    ariaLabel,
    active = false,
    disabled,
    onClick,
    children,
}: ToolbarActionProps) {
    return (
        <Tooltip placement="top" title={label}>
            <Box component="span" sx={{ display: 'inline-flex' }}>
                <Button
                    variant="text"
                    color="inherit"
                    size="small"
                    disabled={disabled}
                    onClick={onClick}
                    aria-label={ariaLabel ?? label}
                    sx={toolbarButtonSx(active)}
                >
                    {children}
                </Button>
            </Box>
        </Tooltip>
    );
}

function ToolbarDivider() {
    return (
        <Divider
            orientation="vertical"
            flexItem
            sx={{ alignSelf: 'center', height: 20, mx: 0.25, borderColor: '#d1d5db' }}
        />
    );
}

interface EditorToolbarProps {
    editor: Editor;
    pricingConfig?: PricingConfiguration;
    branding?: BrandingSettings;
    onBrandingChange?: (branding: BrandingSettings) => void;
    onBrandingReset?: () => void;
    templateType?: TemplateType;
}

export function EditorToolbar({
    editor,
    branding,
    onBrandingChange,
    onBrandingReset,
    templateType = 'visa-application',
}: EditorToolbarProps) {
    const mandatoryPlaceholders = getMandatoryPlaceholdersForType(templateType);
    const placeholdersByCategory = getPlaceholdersByCategoryForType(templateType);
    const categoryLabels = getCategoryLabelsForType(templateType);
    const [linkUrl, setLinkUrl] = useState('');
    const [linkAnchor, setLinkAnchor] = useState<HTMLElement | null>(null);
    const [showTableDialog, setShowTableDialog] = useState(false);
    const [showBrandingPanel, setShowBrandingPanel] = useState(false);
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [tableMenuAnchor, setTableMenuAnchor] = useState<null | HTMLElement>(null);
    const [mandatoryAnchor, setMandatoryAnchor] = useState<null | HTMLElement>(null);
    const [optionalAnchor, setOptionalAnchor] = useState<null | HTMLElement>(null);
    const cursorRef = useRef<{ from: number; to: number } | null>(null);
    const [formatRevision, setFormatRevision] = useState(0);

    useEffect(() => {
        const refresh = () => setFormatRevision((value) => value + 1);
        editor.on('selectionUpdate', refresh);
        editor.on('update', refresh);
        return () => {
            editor.off('selectionUpdate', refresh);
            editor.off('update', refresh);
        };
    }, [editor]);

    const getTextStyleValue = () => {
        if (editor.isActive('heading', { level: 1 })) return 'h1';
        if (editor.isActive('heading', { level: 2 })) return 'h2';
        if (editor.isActive('heading', { level: 3 })) return 'h3';
        return 'normal';
    };

    const toggleTableBorders = (add: boolean) => {
        const { state, view } = editor;
        const { selection, doc, tr } = state;
        let changed = false;

        doc.nodesBetween(0, doc.content.size, (node, pos) => {
            if (node.type.name === 'table') {
                if (pos <= selection.from && pos + node.nodeSize >= selection.to) {
                    doc.nodesBetween(pos, pos + node.nodeSize, (cellNode, cellPos) => {
                        if (
                            cellNode.type.name === 'tableCell' ||
                            cellNode.type.name === 'tableHeader'
                        ) {
                            const currentStyle: string = cellNode.attrs.style || '';
                            const stripped = currentStyle
                                .replace(/\bborder(-\w+)?\s*:[^;]+;?\s*/g, '')
                                .trim();
                            const newStyle = add
                                ? stripped
                                    ? `${stripped}; border: 1px solid #d1d5db`
                                    : 'border: 1px solid #d1d5db'
                                : stripped
                                  ? `${stripped}; border: none`
                                  : 'border: none';
                            tr.setNodeMarkup(cellPos, undefined, {
                                ...cellNode.attrs,
                                style: newStyle,
                            });
                            changed = true;
                        }
                    });
                    return false;
                }
            }
        });

        if (changed) view.dispatch(tr);
    };

    const captureCursor = () => {
        const { from, to } = editor.state.selection;
        cursorRef.current = { from, to };
    };

    const insertFieldText = (token: string) => {
        const range = cursorRef.current ?? {
            from: editor.state.selection.from,
            to: editor.state.selection.to,
        };
        editor
            .chain()
            .focus()
            .command(({ tr }) => {
                tr.insertText(token, range.from, range.to);
                return true;
            })
            .scrollIntoView()
            .run();
        setMandatoryAnchor(null);
        setOptionalAnchor(null);
    };

    const handleInsertTable = (rows: number, cols: number, withHeaderRow: boolean) => {
        editor.chain().focus().insertTable({ rows, cols, withHeaderRow }).run();
    };

    const insertImage = () => {
        const url = window.prompt('Enter image URL:');
        if (url) {
            editor.chain().focus().setImage({ src: url }).run();
        }
    };

    const setLink = () => {
        if (linkUrl) {
            editor.chain().focus().setLink({ href: linkUrl }).run();
            setLinkUrl('');
            setLinkAnchor(null);
        }
    };

    const unsetLink = () => {
        editor.chain().focus().unsetLink().run();
    };

    const setTextAlign = (alignment: 'left' | 'center' | 'right' | 'justify') => {
        editor.chain().focus().setTextAlign(alignment).run();
    };

    const getLineHeightLabel = () => {
        if (!branding) return 'Normal (1.5)';
        const option = LINE_HEIGHT_OPTIONS.find((opt) => opt.value === branding.lineHeight);
        return option ? option.label : branding.lineHeight.toString();
    };

    const activeFontName =
        formatRevision >= 0 ? fontFamilyLabel(editor.getAttributes('textStyle').fontFamily) : null;
    const displayedFont = activeFontName || branding?.fontFamily || DEFAULT_BRANDING.fontFamily;
    const knownFonts = FONT_FAMILIES as readonly string[];
    const fontSizes = [10, 11, 12, 13, 14, 15, 16];
    const bodyFontSize = branding?.bodyFontSize ?? DEFAULT_BRANDING.bodyFontSize;
    const fontSizeOptions = fontSizes.includes(bodyFontSize)
        ? fontSizes
        : [bodyFontSize, ...fontSizes];
    const lineHeight = branding?.lineHeight ?? DEFAULT_BRANDING.lineHeight;
    const lineHeightKnown = LINE_HEIGHT_OPTIONS.some((option) => option.value === lineHeight);

    const runTableAction = (action: () => void) => {
        action();
        setTableMenuAnchor(null);
    };

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.75,
                    bgcolor: '#fafafa',
                }}
            >
                <ToolbarAction
                    label="Undo (Ctrl+Z)"
                    ariaLabel="Undo"
                    disabled={!editor.can().undo()}
                    onClick={() => editor.chain().focus().undo().run()}
                >
                    <Undo />
                </ToolbarAction>
                <ToolbarAction
                    label="Redo (Ctrl+Y)"
                    ariaLabel="Redo"
                    disabled={!editor.can().redo()}
                    onClick={() => editor.chain().focus().redo().run()}
                >
                    <Redo />
                </ToolbarAction>

                <ToolbarDivider />

                <Button
                    variant="text"
                    color="inherit"
                    size="small"
                    onClick={() => setShowBrandingPanel(true)}
                    startIcon={<PaletteOutlined />}
                    sx={toolbarButtonSx()}
                >
                    Branding
                </Button>

                {branding && onBrandingChange && (
                    <Tooltip placement="top" title="Page size">
                        <Select
                            size="small"
                            value={branding.pageSize || 'letter'}
                            aria-label="Page size"
                            onChange={(event) =>
                                onBrandingChange({
                                    ...branding,
                                    pageSize: event.target.value as keyof typeof PAGE_SIZES,
                                })
                            }
                            sx={{ ...toolbarSelectSx, minWidth: 148 }}
                        >
                            {(Object.keys(PAGE_SIZES) as Array<keyof typeof PAGE_SIZES>).map(
                                (key) => (
                                    <MenuItem key={key} value={key}>
                                        {PAGE_SIZES[key].label}
                                    </MenuItem>
                                )
                            )}
                        </Select>
                    </Tooltip>
                )}

                <ToolbarDivider />

                <Tooltip placement="top" title="Text style">
                    <Select
                        size="small"
                        value={getTextStyleValue()}
                        aria-label="Text style"
                        onChange={(event) => {
                            const value = event.target.value;
                            if (value === 'h1' || value === 'h2' || value === 'h3') {
                                editor
                                    .chain()
                                    .focus()
                                    .setHeading({ level: Number(value.slice(1)) as 1 | 2 | 3 })
                                    .run();
                                return;
                            }
                            editor.chain().focus().setParagraph().run();
                        }}
                        renderValue={(value) =>
                            ({
                                normal: 'Normal',
                                h1: 'Heading 1',
                                h2: 'Heading 2',
                                h3: 'Heading 3',
                            })[value] || 'Normal'
                        }
                        sx={{ ...toolbarSelectSx, minWidth: 118 }}
                    >
                        <MenuItem value="normal">
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <TextFields sx={{ fontSize: 18 }} />
                                <span>Normal</span>
                            </Stack>
                        </MenuItem>
                        <MenuItem value="h1">
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <LooksOne sx={{ fontSize: 18 }} />
                                <span>Heading 1</span>
                            </Stack>
                        </MenuItem>
                        <MenuItem value="h2">
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <LooksTwo sx={{ fontSize: 18 }} />
                                <span>Heading 2</span>
                            </Stack>
                        </MenuItem>
                        <MenuItem value="h3">
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <Looks3 sx={{ fontSize: 18 }} />
                                <span>Heading 3</span>
                            </Stack>
                        </MenuItem>
                    </Select>
                </Tooltip>

                <ToolbarDivider />

                {branding && onBrandingChange && (
                    <>
                        <Tooltip placement="top" title="Font Family">
                            <Select
                                size="small"
                                displayEmpty
                                value={activeFontName || ''}
                                aria-label="Font family"
                                onChange={(event) => {
                                    const next = event.target.value;
                                    if (!next) {
                                        editor.chain().focus().unsetFontFamily().run();
                                        return;
                                    }
                                    editor
                                        .chain()
                                        .focus()
                                        .setFontFamily(quoteFontFamily(next))
                                        .run();
                                }}
                                renderValue={() => (
                                    <Box
                                        component="span"
                                        sx={{
                                            fontFamily: quoteFontFamily(displayedFont),
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {displayedFont}
                                    </Box>
                                )}
                                sx={{ ...toolbarSelectSx, minWidth: 140, maxWidth: 180 }}
                            >
                                <MenuItem value="" sx={{ fontWeight: activeFontName ? 400 : 600 }}>
                                    Template default
                                </MenuItem>
                                {activeFontName && !knownFonts.includes(activeFontName) ? (
                                    <MenuItem
                                        value={activeFontName}
                                        sx={{
                                            fontFamily: quoteFontFamily(activeFontName),
                                            fontWeight: 600,
                                        }}
                                    >
                                        {activeFontName}
                                    </MenuItem>
                                ) : null}
                                {FONT_FAMILIES.map((font) => (
                                    <MenuItem
                                        key={font}
                                        value={font}
                                        sx={{
                                            fontFamily: quoteFontFamily(font),
                                            fontWeight: activeFontName === font ? 600 : 400,
                                        }}
                                    >
                                        {font}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Tooltip>

                        <Tooltip placement="top" title="Body Font Size">
                            <Select
                                size="small"
                                value={bodyFontSize}
                                aria-label="Font size"
                                onChange={(event) =>
                                    onBrandingChange({
                                        ...branding,
                                        bodyFontSize: Number(event.target.value),
                                    })
                                }
                                sx={{ ...toolbarSelectSx, minWidth: 76 }}
                            >
                                {fontSizeOptions.map((size) => (
                                    <MenuItem key={size} value={size}>
                                        {size}pt
                                    </MenuItem>
                                ))}
                            </Select>
                        </Tooltip>

                        <Tooltip placement="top" title="Line Height">
                            <Select
                                size="small"
                                value={lineHeight}
                                aria-label="Line height"
                                renderValue={() => getLineHeightLabel()}
                                onChange={(event) =>
                                    onBrandingChange({
                                        ...branding,
                                        lineHeight: Number(event.target.value),
                                    })
                                }
                                sx={{ ...toolbarSelectSx, minWidth: 132 }}
                            >
                                {!lineHeightKnown ? (
                                    <MenuItem value={lineHeight}>{lineHeight}</MenuItem>
                                ) : null}
                                {LINE_HEIGHT_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Tooltip>

                        <ToolbarDivider />
                    </>
                )}

                <ToolbarAction
                    label="Bold (Ctrl+B)"
                    ariaLabel="Bold"
                    active={editor.isActive('bold')}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                >
                    <FormatBold />
                </ToolbarAction>
                <ToolbarAction
                    label="Italic (Ctrl+I)"
                    ariaLabel="Italic"
                    active={editor.isActive('italic')}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                >
                    <FormatItalic />
                </ToolbarAction>
                <ToolbarAction
                    label="Underline (Ctrl+U)"
                    ariaLabel="Underline"
                    active={editor.isActive('underline')}
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                >
                    <FormatUnderlined />
                </ToolbarAction>
                <ToolbarAction
                    label="Strikethrough"
                    active={editor.isActive('strike')}
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                >
                    <StrikethroughS />
                </ToolbarAction>
                <ToolbarAction
                    label="Inline Code"
                    ariaLabel="Code"
                    active={editor.isActive('code')}
                    onClick={() => editor.chain().focus().toggleCode().run()}
                >
                    <Code />
                </ToolbarAction>

                <ToolbarDivider />

                <ColorControl editor={editor} />

                <ToolbarDivider />

                <ToolbarAction
                    label="Align Left"
                    active={editor.isActive({ textAlign: 'left' })}
                    onClick={() => setTextAlign('left')}
                >
                    <FormatAlignLeft />
                </ToolbarAction>
                <ToolbarAction
                    label="Align Center"
                    active={editor.isActive({ textAlign: 'center' })}
                    onClick={() => setTextAlign('center')}
                >
                    <FormatAlignCenter />
                </ToolbarAction>
                <ToolbarAction
                    label="Align Right"
                    active={editor.isActive({ textAlign: 'right' })}
                    onClick={() => setTextAlign('right')}
                >
                    <FormatAlignRight />
                </ToolbarAction>
                <ToolbarAction
                    label="Justify"
                    active={editor.isActive({ textAlign: 'justify' })}
                    onClick={() => setTextAlign('justify')}
                >
                    <FormatAlignJustify />
                </ToolbarAction>

                <ToolbarDivider />

                <ToolbarAction
                    label="Bullet List"
                    active={editor.isActive('bulletList')}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                >
                    <FormatListBulleted />
                </ToolbarAction>
                <ToolbarAction
                    label="Numbered List"
                    active={editor.isActive('orderedList')}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                >
                    <FormatListNumbered />
                </ToolbarAction>
                <ToolbarAction
                    label="Blockquote"
                    active={editor.isActive('blockquote')}
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                >
                    <FormatQuote />
                </ToolbarAction>

                <ToolbarDivider />

                <ToolbarAction
                    label="Insert Link"
                    active={editor.isActive('link')}
                    onClick={(event) => setLinkAnchor(event.currentTarget)}
                >
                    <InsertLink />
                </ToolbarAction>
                <PopoverLink
                    anchorEl={linkAnchor}
                    linkUrl={linkUrl}
                    onClose={() => setLinkAnchor(null)}
                    onChange={setLinkUrl}
                    onInsert={setLink}
                />
                <ToolbarAction
                    label="Remove Link"
                    disabled={!editor.isActive('link')}
                    onClick={unsetLink}
                >
                    <LinkOff />
                </ToolbarAction>

                <ToolbarDivider />

                <ToolbarAction label="Insert Table" onClick={() => setShowTableDialog(true)}>
                    <TableChart />
                </ToolbarAction>

                {editor.isActive('table') && (
                    <>
                        <Button
                            variant="text"
                            color="inherit"
                            size="small"
                            aria-label="Table options"
                            onClick={(event) => setTableMenuAnchor(event.currentTarget)}
                            endIcon={<ExpandMore />}
                            sx={{ ...toolbarButtonSx(), px: 1 }}
                        >
                            Table
                        </Button>
                        <Menu
                            anchorEl={tableMenuAnchor}
                            open={Boolean(tableMenuAnchor)}
                            onClose={() => setTableMenuAnchor(null)}
                            slotProps={{ paper: { sx: { ...menuPaperSx, minWidth: 200 } } }}
                        >
                            <MenuItem
                                onClick={() =>
                                    runTableAction(() =>
                                        editor.chain().focus().addRowBefore().run()
                                    )
                                }
                            >
                                Insert row above
                            </MenuItem>
                            <MenuItem
                                onClick={() =>
                                    runTableAction(() => editor.chain().focus().addRowAfter().run())
                                }
                            >
                                Insert row below
                            </MenuItem>
                            <MenuItem
                                onClick={() =>
                                    runTableAction(() => editor.chain().focus().deleteRow().run())
                                }
                                sx={{ color: 'error.main' }}
                            >
                                <DeleteOutlined sx={{ fontSize: 16, mr: 1 }} />
                                Delete row
                            </MenuItem>
                            <MenuItem
                                onClick={() =>
                                    runTableAction(() =>
                                        editor.chain().focus().addColumnBefore().run()
                                    )
                                }
                            >
                                Insert column left
                            </MenuItem>
                            <MenuItem
                                onClick={() =>
                                    runTableAction(() =>
                                        editor.chain().focus().addColumnAfter().run()
                                    )
                                }
                            >
                                Insert column right
                            </MenuItem>
                            <MenuItem
                                onClick={() =>
                                    runTableAction(() =>
                                        editor.chain().focus().deleteColumn().run()
                                    )
                                }
                                sx={{ color: 'error.main' }}
                            >
                                <DeleteOutlined sx={{ fontSize: 16, mr: 1 }} />
                                Delete column
                            </MenuItem>
                            <MenuItem
                                onClick={() =>
                                    runTableAction(() => editor.chain().focus().mergeCells().run())
                                }
                            >
                                Merge cells
                            </MenuItem>
                            <MenuItem
                                onClick={() =>
                                    runTableAction(() => editor.chain().focus().splitCell().run())
                                }
                            >
                                Split cell
                            </MenuItem>
                            <MenuItem
                                onClick={() =>
                                    runTableAction(() =>
                                        editor.chain().focus().toggleHeaderRow().run()
                                    )
                                }
                            >
                                Toggle header row
                            </MenuItem>
                            <MenuItem
                                onClick={() => runTableAction(() => toggleTableBorders(true))}
                            >
                                Add borders
                            </MenuItem>
                            <MenuItem
                                onClick={() => runTableAction(() => toggleTableBorders(false))}
                            >
                                Remove borders
                            </MenuItem>
                            <MenuItem
                                onClick={() =>
                                    runTableAction(() => editor.chain().focus().deleteTable().run())
                                }
                                sx={{ color: 'error.main' }}
                            >
                                <DeleteOutlined sx={{ fontSize: 16, mr: 1 }} />
                                Delete table
                            </MenuItem>
                        </Menu>
                    </>
                )}

                <ToolbarAction label="Insert Image" onClick={insertImage}>
                    <ImageOutlined />
                </ToolbarAction>
                <ToolbarAction
                    label="Horizontal Rule"
                    onClick={() => editor.chain().focus().setHorizontalRule().run()}
                >
                    <HorizontalRule />
                </ToolbarAction>
                <ToolbarAction
                    label="Page Break (Ctrl+Enter)"
                    ariaLabel="Page break"
                    onClick={() => editor.chain().focus().setPageBreak().run()}
                >
                    <DescriptionOutlined />
                </ToolbarAction>

                <ToolbarDivider />

                <ToolbarAction
                    label="Clear Formatting"
                    onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
                >
                    <FormatClear />
                </ToolbarAction>

                <ToolbarDivider />

                <Tooltip placement="top" title={MESSAGES.TOOLTIPS.MANDATORY_PLACEHOLDER}>
                    <Button
                        variant="text"
                        color="inherit"
                        size="small"
                        aria-label="Mandatory placeholders"
                        onMouseDown={(event) => {
                            event.preventDefault();
                            captureCursor();
                        }}
                        onClick={(event) => setMandatoryAnchor(event.currentTarget)}
                        startIcon={<LockOutlined />}
                        sx={toolbarButtonSx()}
                    >
                        Mandatory
                    </Button>
                </Tooltip>
                <Menu
                    anchorEl={mandatoryAnchor}
                    open={Boolean(mandatoryAnchor)}
                    onClose={() => setMandatoryAnchor(null)}
                    autoFocus={false}
                    disableAutoFocus
                    disableAutoFocusItem
                    disableEnforceFocus
                    disableRestoreFocus
                    slotProps={{ paper: { sx: { ...menuPaperSx, width: 320 } } }}
                >
                    <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Mandatory Placeholders
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            These placeholders are required and cannot be deleted
                        </Typography>
                    </Box>
                    {mandatoryPlaceholders.map((placeholder) => (
                        <MenuItem
                            key={placeholder.token}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => insertFieldText(placeholder.token)}
                            sx={{ alignItems: 'flex-start', whiteSpace: 'normal' }}
                        >
                            <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {placeholder.label}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontFamily: 'monospace' }}
                                >
                                    {placeholder.token}
                                </Typography>
                            </Box>
                        </MenuItem>
                    ))}
                </Menu>

                <Tooltip placement="top" title="Insert optional placeholder fields">
                    <Button
                        variant="text"
                        color="inherit"
                        size="small"
                        aria-label="Optional placeholders"
                        onMouseDown={(event) => {
                            event.preventDefault();
                            captureCursor();
                        }}
                        onClick={(event) => setOptionalAnchor(event.currentTarget)}
                        startIcon={<DataObject />}
                        sx={toolbarButtonSx()}
                    >
                        Optional
                    </Button>
                </Tooltip>
                <Menu
                    anchorEl={optionalAnchor}
                    open={Boolean(optionalAnchor)}
                    onClose={() => setOptionalAnchor(null)}
                    autoFocus={false}
                    disableAutoFocus
                    disableAutoFocusItem
                    disableEnforceFocus
                    disableRestoreFocus
                    slotProps={{ paper: { sx: { ...menuPaperSx, width: 320 } } }}
                >
                    <ListSubheader sx={{ bgcolor: 'background.paper', lineHeight: '32px' }}>
                        Optional Placeholders
                    </ListSubheader>
                    {Object.entries(placeholdersByCategory).map(([category, placeholders]) => {
                        const optionalInCategory = placeholders.filter(
                            (placeholder) => !placeholder.mandatory
                        );
                        if (optionalInCategory.length === 0) return null;

                        return [
                            <ListSubheader
                                key={`${category}-label`}
                                sx={{
                                    bgcolor: 'background.paper',
                                    lineHeight: '28px',
                                    fontSize: 11,
                                }}
                            >
                                {categoryLabels[category]}
                            </ListSubheader>,
                            ...optionalInCategory.map((placeholder) => (
                                <MenuItem
                                    key={placeholder.token}
                                    onMouseDown={(event) => event.preventDefault()}
                                    onClick={() => insertFieldText(placeholder.token)}
                                    sx={{ alignItems: 'flex-start', whiteSpace: 'normal' }}
                                >
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {placeholder.label}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ fontFamily: 'monospace' }}
                                        >
                                            {placeholder.token}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            )),
                        ];
                    })}
                </Menu>
            </Box>

            <TableInsertDialog
                open={showTableDialog}
                onOpenChange={setShowTableDialog}
                onInsert={handleInsertTable}
            />

            <Dialog
                open={showBrandingPanel}
                onClose={() => setShowBrandingPanel(false)}
                fullWidth
                maxWidth="sm"
                fullScreen={isMobile}
                scroll="paper"
                aria-labelledby="branding-layout-title"
                slotProps={{
                    paper: {
                        sx: {
                            height: { xs: '100%', sm: 'min(680px, 90vh)' },
                        },
                    },
                }}
            >
                <DialogTitle
                    component="div"
                    id="branding-layout-title"
                    sx={{
                        px: { xs: 2, sm: 2.5 },
                        py: 1.5,
                        borderBottom: 1,
                        borderColor: 'divider',
                    }}
                >
                    <Stack
                        direction="row"
                        spacing={1.5}
                        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700 }}>
                                {translate('brandingAndLayout')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {translate('brandingAndLayoutDescription')}
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            onClick={() => setShowBrandingPanel(false)}
                            aria-label={translate('close')}
                        >
                            <Close fontSize="small" />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent
                    sx={{
                        p: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                    }}
                >
                    <BrandingPanel
                        branding={branding || DEFAULT_BRANDING}
                        onBrandingChange={onBrandingChange || (() => undefined)}
                    />
                </DialogContent>
                <DialogActions sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5, gap: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            if (onBrandingReset) {
                                onBrandingReset();
                                return;
                            }
                            onBrandingChange?.(DEFAULT_BRANDING);
                        }}
                    >
                        {translate('resetToDefaults')}
                    </Button>
                    <Button variant="contained" onClick={() => setShowBrandingPanel(false)}>
                        {translate('done')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

function PopoverLink({
    anchorEl,
    linkUrl,
    onClose,
    onChange,
    onInsert,
}: {
    anchorEl: HTMLElement | null;
    linkUrl: string;
    onClose: () => void;
    onChange: (value: string) => void;
    onInsert: () => void;
}) {
    return (
        <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            slotProps={{ paper: { sx: { p: 2, width: 320 } } }}
        >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Insert Link
            </Typography>
            <Stack direction="row" spacing={1}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="https://example.com"
                    value={linkUrl}
                    onChange={(event) => onChange(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            event.preventDefault();
                            onInsert();
                        }
                    }}
                    aria-label="Link URL"
                />
                <Button
                    variant="contained"
                    size="small"
                    onClick={onInsert}
                    sx={{ textTransform: 'none' }}
                >
                    Insert
                </Button>
            </Stack>
        </Popover>
    );
}
